import {
  useContext,
  useState,
  useEffect,
  type ReactElement,
  FC,
  cloneElement,
  MutableRefObject,
} from "react";
import APIContext from "../contexts/APIContext";
import { SolutionClasses } from "../types/SolutionClasses";
import NotificationToast from "./notification-toast";
import {
  Box,
  Colors,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@analyticsMD/quil";
import Divider from "@mui/material/Divider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceholderSection from "./sections/placeholder-section";
import DischargePlanSection from "./sections/discharge-plan-section";
import PatientInfoSection from "./sections/patient-info-section";
import PhysicianReviewedBanner from "./physician-reviewed-banner";
import { ReactNode } from "react-markdown";
import { buildSectionRegistry, splitForLayout } from "./qcard-sections.helpers";

const TOAST_ERROR_MESSAGE =
  "We encountered an issue while updating patient data. Please try again";

const mainStyles = {
  columns: { display: "flex", flexDirection: "column" },
};

interface QCardSectionProps {
  useSidebarView?: boolean;
  updateRef: MutableRefObject<Record<string, any>>;
  closeButtonCmpt?: ReactNode;
}

const QCardSections: FC<QCardSectionProps> = ({
  useSidebarView = false,
  updateRef = null,
  closeButtonCmpt = null,
}) => {
  const [showDemographics, setShowDemographics] = useState(false);
  const {
    patientQuery,
    configurationQuery,
    showErrorToast,
    setShowErrorToast,
    isViewOnly,
    patientDataReadOnly,
    productionDataReadOnly,
    activeRoundingSessionQuery,
    qCardReviewStatusQuery,
  } = useContext(APIContext);

  const activeRoundingSession =
    activeRoundingSessionQuery?.isSuccess && activeRoundingSessionQuery.data;

  const isEligible = patientQuery.data?.eligible_classes?.some((ec) =>
    [
      SolutionClasses.ACTIVE_UNIT,
      SolutionClasses.DISCHARGE_PLANNING,
      SolutionClasses.DISCHARGE_SUPPORT,
      SolutionClasses.OBSERVATION_PLANNING,
      SolutionClasses.OBSERVATION_SUPPORT,
    ].includes(ec)
  );

  const isWideLayout = useMediaQuery("(min-width:62.5rem)");
  const useWideLayout = useSidebarView ? false : isWideLayout;
  const urlParams = new URLSearchParams(window.location.search);
  const inRoundsMode =
    urlParams.get("emr_mode")?.toLowerCase() === "rounds" || useSidebarView;

  useEffect(() => {
    setShowDemographics(!inRoundsMode);
  }, [inRoundsMode]);

  if (!isEligible && !isViewOnly) {
    return <PlaceholderSection closeButtonCmpt={closeButtonCmpt} />;
  }

  const configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : {};

  // Orderable sections come from the admin-configured Section Order. The
  // registry returns null for sections disabled via feature flags; splitForLayout
  // filters those out uniformly across narrow + wide.
  const sectionRegistry = buildSectionRegistry(configuration);
  const { narrowSections, leftColumn, rightColumn } = splitForLayout(
    configuration.qcard_layout_section_order,
    sectionRegistry
  );
  const rightColumnEnabled = rightColumn.length > 0;

  const generateSections = () => {
    if (useWideLayout) {
      return (
        <Box sx={{ ...mainStyles.columns, background: Colors.grey_10 }}>
          <Grid container spacing={0.5} columns={rightColumnEnabled ? 2 : 1}>
            <Grid item xs={1}>
              <Stack spacing={0.5} sx={{ paddingBottom: "0.5rem" }}>
                {/* DischargePlan is a fixed header pinned to the top of the
                    left column (alongside PatientInfo above), not part of
                    the orderable list. */}
                <DischargePlanSection />
                {leftColumn}
              </Stack>
            </Grid>
            {rightColumnEnabled ? (
              <Grid item xs={1}>
                <Stack spacing={0.5} sx={{ height: "100%" }}>
                  {rightColumn}
                </Stack>
              </Grid>
            ) : null}
          </Grid>
          <NotificationToast
            message={TOAST_ERROR_MESSAGE}
            showToast={showErrorToast}
            isError={true}
            onClose={() => setShowErrorToast(false)}
          />
        </Box>
      );
    }
    const divider = <Divider sx={{ margin: "0.5rem 1rem" }} />;
    const dischargeSection = (
      <DischargePlanSection
        key="discharge"
        useSidebarView={useSidebarView}
        updateRef={updateRef}
      />
    );

    // DischargePlan stays pinned as the first item in the narrow stack, then
    // the configured orderable sections follow with the same divider pattern
    // the previous implementation used.
    const allSections: ReactElement[] = [dischargeSection, ...narrowSections];
    const content = allSections.flatMap((section, index) =>
      index === 0
        ? section
        : [cloneElement(divider, { key: `divider-${index}` }), section]
    );

    return (
      <Box
        sx={{
          position: "relative",
        }}
      >
        {content}
        <NotificationToast
          message={TOAST_ERROR_MESSAGE}
          showToast={showErrorToast}
          isError={true}
          onClose={() => setShowErrorToast(false)}
        />
      </Box>
    );
  };

  return (
    <>
      {isViewOnly && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem 1rem",
            backgroundColor: Colors.light_30,
            borderBottom: `1px solid ${Colors.grey_20}`,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: "1rem", color: Colors.grey_40 }} />
          <Typography sx={{ fontSize: "0.812rem", color: Colors.grey_60 }}>
            This patient has been discharged. QCard is view-only.
          </Typography>
        </Box>
      )}
      {productionDataReadOnly && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem 1rem",
            backgroundColor: Colors.light_30,
            borderBottom: `1px solid ${Colors.grey_20}`,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: "1rem", color: Colors.grey_40 }} />
          <Typography sx={{ fontSize: "0.812rem", color: Colors.grey_60 }}>
            Production environment: patient data is view-only. Contact your team
            if you need edit access.
          </Typography>
        </Box>
      )}
      <PatientInfoSection
        showRoundingMode={!patientDataReadOnly || !!activeRoundingSession}
        showDemographics={showDemographics}
        useSidebarView={useSidebarView}
        closeButtonCmpt={closeButtonCmpt}
      />
      {configurationQuery?.isSuccess &&
      configurationQuery.data?.[0]?.async_physician_review_enabled &&
      qCardReviewStatusQuery?.isSuccess &&
      qCardReviewStatusQuery.data?.status ? (
        <PhysicianReviewedBanner reviewStatus={qCardReviewStatusQuery.data} />
      ) : null}
      {generateSections()}
    </>
  );
};

export default QCardSections;
